import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { adminService } from '../../services/apiService';
import AdminCampaignDashboard from './AdminCampaignDashboard';
import AdminRegistrationApprovals from './AdminRegistrationApprovals';

const AdminContainer = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  border-bottom: 3px solid rgba(102, 126, 234, 0.2);
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 0.5rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    gap: 0;
    margin-bottom: 1rem;
  }
`;

const Tab = styled.button`
  padding: 0.8rem 1.6rem;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${props => props.active ? '#1e293b' : '#6b7280'};
  border-bottom: ${props => props.active ? '3px solid #1e293b' : '3px solid transparent'};
  transition: all 0.3s ease;
  margin-bottom: -3px;
  white-space: nowrap;

  &:hover {
    color: #1e293b;
  }

  @media (max-width: 768px) {
    padding: 0.8rem 1.2rem;
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    padding: 0.6rem 0.7rem;
    font-size: 0.75rem;
    margin: 0;
    border-bottom: 2px solid transparent;
    border-radius: 8px 8px 0 0;
    background: ${props => props.active ? '#1e293b' : '#f0f0f0'};
    color: ${props => props.active ? 'white' : '#666'};
    margin-bottom: 0;
  }
`;

const ListingsTable = styled.div`
  width: 100%;
  background: white;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  overflow: hidden;
`;

const AdminTableHeader = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1.2fr 1.2fr 1fr 1fr 1fr 1.5fr;
  gap: 1rem;
  padding: 1.2rem;
  background: #1e293b;
  color: white;
  font-weight: 700;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border-bottom: 2px solid #667eea;
  align-items: center;

  @media (max-width: 1024px) {
    grid-template-columns: 1.5fr 1fr 1fr 0.8fr 0.8fr 0.8fr 1.2fr;
    gap: 0.75rem;
    padding: 0.9rem 1rem;
    font-size: 0.75rem;
  }

  @media (max-width: 600px) {
    display: none;
  }
`;

const AdminTableRow = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1.2fr 1.2fr 1fr 1fr 1fr 1.5fr;
  gap: 1rem;
  padding: 1rem 1.2rem;
  border-bottom: 1px solid rgba(102, 126, 234, 0.1);
  align-items: center;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:nth-child(even) {
    background: rgba(102, 126, 234, 0.02);
  }

  &:hover {
    background: rgba(102, 126, 234, 0.08);
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1.5fr 1fr 1fr 0.8fr 0.8fr 0.8fr 1.2fr;
    gap: 0.75rem;
    padding: 0.9rem 1rem;
    font-size: 0.8rem;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
    padding: 1rem 0.75rem;
    border-left: 4px solid #667eea;
    margin-bottom: 0.75rem;
    background: white;
    border-radius: 8px;
    border-bottom: 1px solid rgba(102, 126, 234, 0.1);
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;

  button {
    padding: 0.5rem 0.9rem;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 600;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.15px;
    transition: all 0.3s ease;
    min-height: 32px;

    &.view {
      background: #1e293b;
      color: white;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

      &:hover {
        transform: translateY(-1px);
        background: #0f172a;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }
    }

    &.approve {
      background: #059669;
      color: white;
      box-shadow: 0 2px 8px rgba(5, 150, 105, 0.15);

      &:hover {
        transform: translateY(-1px);
        background: #047857;
        box-shadow: 0 4px 12px rgba(5, 150, 105, 0.25);
      }
    }

    &.reject {
      background: #dc2626;
      color: white;
      box-shadow: 0 2px 8px rgba(220, 38, 38, 0.15);

      &:hover {
        transform: translateY(-1px);
        background: #b91c1c;
        box-shadow: 0 4px 12px rgba(220, 38, 38, 0.25);
      }
    }

    @media (max-width: 768px) {
      padding: 0.5rem 0.9rem;
      font-size: 0.7rem;
      min-height: 32px;
    }

    @media (max-width: 480px) {
      padding: 0.4rem 0.7rem;
      font-size: 0.65rem;
      min-height: 30px;
    }
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  overflow-y: auto;
  padding: 1rem;

  @media (max-width: 480px) {
    padding: 0.5rem;
  }
`;

const ModalContent = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
  margin: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);

  h2 {
    margin-top: 0;
    color: #2c3e50;
    font-weight: 700;
  }

  textarea {
    width: 100%;
    min-height: 100px;
    padding: 0.8rem;
    margin: 1rem 0;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    font-family: inherit;
    resize: vertical;

    &:focus {
      outline: none;
      border-color: #667eea;
      box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    }
  }

  button {
    padding: 0.8rem 1.6rem;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    margin-right: 1rem;
    font-weight: 700;
    transition: all 0.3s ease;
  }

  .confirm {
    background: linear-gradient(135deg, #f44336 0%, #da190b 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(244, 67, 54, 0.2);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(244, 67, 54, 0.3);
    }
  }

  .cancel {
    background: #e0e0e0;
    color: #666;
    font-weight: 700;

    &:hover {
      background: #d0d0d0;
    }
  }

  @media (max-width: 480px) {
    padding: 1.5rem 1.25rem;

    textarea {
      min-height: 80px;
    }

    button {
      padding: 0.7rem 1.2rem;
      margin-right: 0.5rem;
      font-size: 0.85rem;
    }
  }
`;

const DetailsModalContent = styled.div`
  background: white;
  border-radius: 12px;
  max-width: 700px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  margin: auto;

  .modal-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 2rem;
    color: white;
    border-radius: 12px 12px 0 0;
    display: flex;
    justify-content: space-between;
    align-items: center;

    h2 {
      margin: 0;
      font-size: 1.5rem;
      font-weight: 800;
      letter-spacing: -0.3px;
    }

    button {
      background: rgba(255, 255, 255, 0.2);
      border: none;
      color: white;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      font-size: 1.5rem;
      cursor: pointer;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover {
        background: rgba(255, 255, 255, 0.3);
      }
    }

    @media (max-width: 480px) {
      padding: 1.5rem;

      h2 {
        font-size: 1.2rem;
      }

      button {
        width: 36px;
        height: 36px;
        font-size: 1.3rem;
      }
    }
  }

  .modal-body {
    padding: 2rem;

    .detail-section {
      margin-bottom: 2rem;
      padding-bottom: 2rem;
      border-bottom: 2px solid rgba(102, 126, 234, 0.1);

      &:last-child {
        border-bottom: none;
      }

      h3 {
        margin: 0 0 1rem 0;
        color: #667eea;
        font-size: 1.1rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .detail-row {
        display: flex;
        justify-content: space-between;
        margin-bottom: 0.8rem;
        padding: 0.6rem 0;

        label {
          font-weight: 700;
          color: #2c3e50;
          width: 40%;
        }

        value {
          color: #667eea;
          font-weight: 600;
        }
      }
    }

    @media (max-width: 480px) {
      padding: 1.5rem 1.25rem;

      .detail-section {
        margin-bottom: 1.5rem;
        padding-bottom: 1.5rem;

        h3 {
          font-size: 0.95rem;
        }

        .detail-row {
          flex-direction: column;

          label {
            width: 100%;
            margin-bottom: 0.3rem;
            font-size: 0.85rem;
          }

          value {
            font-size: 0.85rem;
          }
        }
      }
    }
  }

  .modal-actions {
    display: flex;
    gap: 1rem;
    padding: 1.5rem 2rem;
    border-top: 2px solid rgba(102, 126, 234, 0.1);
    justify-content: flex-end;
    flex-wrap: wrap;

    button {
      padding: 0.8rem 1.6rem;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      font-weight: 700;
      transition: all 0.3s ease;
      min-height: 40px;

      &.approve {
        background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
        color: white;
        box-shadow: 0 4px 12px rgba(76, 175, 80, 0.2);

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(76, 175, 80, 0.3);
        }
      }

      &.reject {
        background: linear-gradient(135deg, #f44336 0%, #da190b 100%);
        color: white;
        box-shadow: 0 4px 12px rgba(244, 67, 54, 0.2);

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(244, 67, 54, 0.3);
        }
      }

      &.close {
        background: #e0e0e0;
        color: #666;
        font-weight: 700;

        &:hover {
          background: #d0d0d0;
        }
      }
    }

    @media (max-width: 480px) {
      padding: 1.25rem;
      gap: 0.5rem;

      button {
        padding: 0.7rem 1.2rem;
        font-size: 0.8rem;
        min-height: 36px;
        flex: 1;
        min-width: 90px;
      }
    }
  }
`;

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('listings');
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailsModal, setDetailsModal] = useState({ open: false, listing: null });
  const [rejectModal, setRejectModal] = useState({ open: false, listingId: null });
  const [rejectionReason, setRejectionReason] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPendingListings();
  }, []);

  const fetchPendingListings = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('📊 [AdminDashboard] Fetching pending listings...');
      const data = await adminService.getPendingListings();
      console.log('✅ [AdminDashboard] Pending listings:', data);
      setListings(data);
    } catch (error) {
      console.error('❌ [AdminDashboard] Error fetching listings:', error);
      setError(error.message || 'Failed to load pending listings');
    }
    setLoading(false);
  };

  const handleApprove = async (listingId) => {
    try {
      await adminService.approveListing(listingId, 1); // adminId should come from context
      setDetailsModal({ open: false, listing: null });
      fetchPendingListings();
    } catch (error) {
      console.error('Error approving listing:', error);
    }
  };

  const handleViewDetails = (listing) => {
    setDetailsModal({ open: true, listing });
  };

  const handleRejectClick = (listingId) => {
    setRejectModal({ open: true, listingId });
    setDetailsModal({ open: false, listing: null });
  };

  const handleRejectConfirm = async () => {
    try {
      await adminService.rejectListing(rejectModal.listingId, 1, rejectionReason);
      setRejectModal({ open: false, listingId: null });
      setRejectionReason('');
      fetchPendingListings();
    } catch (error) {
      console.error('Error rejecting listing:', error);
    }
  };

  return (
    <AdminContainer>
      <h1>Admin Dashboard</h1>
      
      <TabsContainer>
        <Tab 
          active={activeTab === 'listings'} 
          onClick={() => setActiveTab('listings')}
        >
          📋 Pending Listings
        </Tab>
        <Tab 
          active={activeTab === 'campaigns'} 
          onClick={() => setActiveTab('campaigns')}
        >
          🎯 Buying Campaigns
        </Tab>
        <Tab 
          active={activeTab === 'registrations'} 
          onClick={() => setActiveTab('registrations')}
        >
          👥 Registration Approvals
        </Tab>
      </TabsContainer>

      {error && (
        <div style={{
          background: '#fee2e2',
          border: '1px solid #fca5a5',
          color: '#7f1d1d',
          padding: '1rem',
          marginBottom: '1rem',
          marginTop: '1rem',
          borderRadius: '6px',
          fontWeight: 'bold'
        }}>
          ⚠️ Error: {error}
        </div>
      )}

      {activeTab === 'listings' && (
        <>
          {loading ? (
            <p>Loading listings...</p>
          ) : listings.length === 0 ? (
            <p>No pending listings</p>
          ) : (
            <ListingsTable>
              <AdminTableHeader>
                <div>Seller</div>
                <div>Crop Type</div>
                <div>Variety</div>
                <div>State</div>
                <div>Region</div>
                <div>Submitted</div>
                <div>Actions</div>
              </AdminTableHeader>
              {listings.map((listing) => (
                <AdminTableRow key={listing.listingId}>
                  <div>{listing.sellerName}</div>
                  <div>{listing.cropType}</div>
                  <div>{listing.breedVariety}</div>
                  <div>{listing.state}</div>
                  <div>{listing.region}</div>
                  <div>{new Date(listing.createdAt).toLocaleDateString()}</div>
                  <div>
                    <ActionButtons>
                      <button
                        className="view"
                        onClick={() => handleViewDetails(listing)}
                      >
                        👁️ View
                      </button>
                      <button
                        className="approve"
                        onClick={() => handleApprove(listing.listingId)}
                      >
                        ✓ Approve
                      </button>
                      <button
                        className="reject"
                        onClick={() => handleRejectClick(listing.listingId)}
                      >
                        ✕ Reject
                      </button>
                    </ActionButtons>
                  </div>
                </AdminTableRow>
              ))}
            </ListingsTable>
          )}
        </>
      )}

      {activeTab === 'campaigns' && (
        <AdminCampaignDashboard />
      )}

      {activeTab === 'registrations' && (
        <AdminRegistrationApprovals />
      )}

      {activeTab === 'listings' && rejectModal.open && (
        <Modal onClick={() => setRejectModal({ open: false, listingId: null })}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <h2>Reject Listing</h2>
            <p>Please provide a reason for rejection:</p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
            />
            <div>
              <button className="confirm" onClick={handleRejectConfirm}>
                Reject
              </button>
              <button
                className="cancel"
                onClick={() => setRejectModal({ open: false, listingId: null })}
              >
                Cancel
              </button>
            </div>
          </ModalContent>
        </Modal>
      )}

      {activeTab === 'listings' && detailsModal.open && detailsModal.listing && (
        <Modal onClick={() => setDetailsModal({ open: false, listing: null })}>
          <DetailsModalContent onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📋 Listing Details</h2>
              <button onClick={() => setDetailsModal({ open: false, listing: null })}>✕</button>
            </div>

            <div className="modal-body">
              <div className="detail-section">
                <h3>🌾 Crop Information</h3>
                <div className="detail-row">
                  <label>Crop Type:</label>
                  <value>{detailsModal.listing.cropType}</value>
                </div>
                <div className="detail-row">
                  <label>Variety/Breed:</label>
                  <value>{detailsModal.listing.breedVariety || 'N/A'}</value>
                </div>
                <div className="detail-row">
                  <label>Harvest Quantity:</label>
                  <value>{detailsModal.listing.harvestQuantityInKg?.toLocaleString()} kg</value>
                </div>
                <div className="detail-row">
                  <label>Cultivation Area:</label>
                  <value>{detailsModal.listing.cultivationAreaInAcres} acres</value>
                </div>
                <div className="detail-row">
                  <label>Minimum Order:</label>
                  <value>{detailsModal.listing.minimumOrderQuantity} kg</value>
                </div>
              </div>

              <div className="detail-section">
                <h3>💰 Pricing Information</h3>
                <div className="detail-row">
                  <label>Base Price:</label>
                  <value>₹{detailsModal.listing.basePrice?.toFixed(2)}/kg</value>
                </div>
              </div>

              <div className="detail-section">
                <h3>📍 Location Details</h3>
                <div className="detail-row">
                  <label>State:</label>
                  <value>{detailsModal.listing.state}</value>
                </div>
                <div className="detail-row">
                  <label>Region:</label>
                  <value>{detailsModal.listing.region}</value>
                </div>
              </div>

              <div className="detail-section">
                <h3>👨‍🌾 Seller Information</h3>
                <div className="detail-row">
                  <label>Seller Name:</label>
                  <value>{detailsModal.listing.sellerName}</value>
                </div>
              </div>

              <div className="detail-section">
                <h3>⏰ Listing Timeline</h3>
                <div className="detail-row">
                  <label>Submitted:</label>
                  <value>{new Date(detailsModal.listing.createdAt).toLocaleString()}</value>
                </div>
                <div className="detail-row">
                  <label>Status:</label>
                  <value>{detailsModal.listing.status}</value>
                </div>
              </div>

              {detailsModal.listing.description && (
                <div className="detail-section">
                  <h3>📝 Description</h3>
                  <div className="detail-row">
                    <value>{detailsModal.listing.description}</value>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button className="approve" onClick={() => handleApprove(detailsModal.listing.listingId)}>
                ✓ Approve Listing
              </button>
              <button className="reject" onClick={() => handleRejectClick(detailsModal.listing.listingId)}>
                ✕ Reject Listing
              </button>
              <button className="close" onClick={() => setDetailsModal({ open: false, listing: null })}>
                Close
              </button>
            </div>
          </DetailsModalContent>
        </Modal>
      )}
    </AdminContainer>
  );
};

export default AdminDashboard;
